import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader, Select } from '@mantine/core';
import type { ReferenceCodeDto } from '@expeditor/shared';

interface Props {
  /** Поиск по справочнику/классификатору. */
  fetchOptions: (q: string) => Promise<ReferenceCodeDto[]>;
  /** Разрешение строки по коду (для показа выбранного при загрузке). */
  fetchByCode: (code: string) => Promise<ReferenceCodeDto | null>;
  label?: ReactNode;
  value: string;
  onChange: (code: string) => void;
  /** Выбран вариант (для побочной логики — переходник, опасность и т.п.). */
  onOptionPick?: (row: ReferenceCodeDto | null) => void;
  placeholder?: string;
  clearable?: boolean;
  withAsterisk?: boolean;
  error?: ReactNode;
}

/**
 * Поле выбора кода из справочника (только выбор, без произвольного ввода).
 * В поле показывается выбранное значение как «код — наименование»; длинное
 * значение обрезается с многоточием. Поиск — контекстный (серверный).
 */
export function CodeSelect({
  fetchOptions,
  fetchByCode,
  label,
  value,
  onChange,
  onOptionPick,
  placeholder,
  clearable = true,
  withAsterisk,
  error,
}: Props) {
  const [options, setOptions] = useState<ReferenceCodeDto[]>([]);
  const [selectedOpt, setSelectedOpt] = useState<ReferenceCodeDto | null>(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const infoRef = useRef(new Map<string, ReferenceCodeDto>());

  // Показать выбранное значение (метку) для текущего кода — при загрузке/подстановке.
  useEffect(() => {
    if (!value) {
      setSelectedOpt(null);
      return;
    }
    if (selectedOpt?.code === value) return;
    const known = infoRef.current.get(value);
    if (known) {
      setSelectedOpt(known);
      return;
    }
    let cancelled = false;
    fetchByCode(value)
      .then((r) => {
        if (!cancelled && r) {
          infoRef.current.set(r.code, r);
          setSelectedOpt(r);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const search = (q: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const id = ++reqId.current;
      setLoading(true);
      fetchOptions(q)
        .then((res) => {
          if (id !== reqId.current) return;
          res.forEach((r) => infoRef.current.set(r.code, r));
          setOptions(res);
        })
        .catch(() => {
          if (id === reqId.current) setOptions([]);
        })
        .finally(() => {
          if (id === reqId.current) setLoading(false);
        });
    }, 250);
  };

  const data = useMemo(() => {
    const map = new Map<string, ReferenceCodeDto>();
    // Текущее значение всегда должно присутствовать в data, иначе Mantine Select
    // не покажет программно проставленный код (метка = код, пока не подтянется имя).
    if (value) map.set(value, { code: value, name: '' });
    if (selectedOpt) map.set(selectedOpt.code, selectedOpt);
    for (const o of options) map.set(o.code, o);
    return [...map.values()].map((o) => ({
      value: o.code,
      label: o.name ? `${o.code} — ${o.name}` : o.code,
    }));
  }, [value, selectedOpt, options]);

  return (
    <Select
      label={label}
      placeholder={placeholder ?? 'Начните вводить код или название'}
      searchable
      clearable={clearable}
      withAsterisk={withAsterisk}
      error={error}
      value={value || null}
      data={data}
      filter={({ options: opts }) => opts}
      nothingFoundMessage={loading ? 'Поиск…' : 'Ничего не найдено'}
      rightSection={loading ? <Loader size="xs" /> : undefined}
      comboboxProps={{ withinPortal: true }}
      styles={{ input: { textOverflow: 'ellipsis' } }}
      onDropdownOpen={() => search('')}
      onSearchChange={search}
      onChange={(v) => {
        const row = v ? infoRef.current.get(v) ?? selectedOpt : null;
        if (v) setSelectedOpt(row ?? null);
        else setSelectedOpt(null);
        onChange(v ?? '');
        onOptionPick?.(v ? row : null);
      }}
    />
  );
}
